import React, { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from '@mui/material';
import { answerLabel, schoolApi, showTime } from '../../common/schoolApi';
import { QuestionView } from '../school-test/QuestionView';
import { STATE } from './constants';

/**
 * One student's paper, marked (GET /assignment/student_result/5/1234).
 * The counting attempt first; any other attempt of theirs on request.
 */
export const StudentPaperDialog = ({ assignmentId, userId, onClose }: { assignmentId: number; userId: number; onClose: () => void }) => {
    const notify = useNotify();
    const [data, setData] = useState<any>(null);
    const [attemptId, setAttemptId] = useState<number | null>(null);

    useEffect(() => {
        schoolApi
            .get(`assignment/student_result/${assignmentId}/${userId}`, { attempt_id: attemptId })
            .then(r => setData(r.result))
            .catch(e => {
                notify(e.message, { type: 'error' });
                onClose();
            });
    }, [assignmentId, userId, attemptId]);

    const st = data?.student;
    const at = data?.attempt;
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="md" scroll="paper">
            <DialogTitle>
                {st ? `${st.full_name}${st.roll_number ? ` (${st.roll_number})` : ''} — ${st.section}` : 'Loading…'}
            </DialogTitle>
            <DialogContent dividers>
                {data && (
                    <>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                            <Chip size="small" color={STATE[st.state]?.colour} label={STATE[st.state]?.label} />
                            {data.attempts.length > 1 &&
                                data.attempts.map((x: any, i: number) => (
                                    <Chip
                                        key={x.id}
                                        size="small"
                                        clickable
                                        variant={at && at.id === x.id ? 'filled' : 'outlined'}
                                        color={x.counts ? 'primary' : 'default'}
                                        onClick={() => setAttemptId(x.id)}
                                        label={`Attempt ${i + 1}: ${x.score} (${x.percentage}%)${x.counts ? ' — counts' : ''}`}
                                    />
                                ))}
                        </Box>

                        {!at ? (
                            <Typography>No attempt yet.</Typography>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Score <b>{at.totals.total_score}</b> of {at.totals.total_marks} ({at.totals.percentage}%) — {at.totals.correct} right,{' '}
                                    {at.totals.incorrect} wrong, {at.totals.total_questions - at.totals.answered} blank.
                                    {data.attempts.find((x: any) => x.id === at.id)?.completed_at &&
                                        ` Finished ${showTime(data.attempts.find((x: any) => x.id === at.id).completed_at)}.`}
                                </Typography>
                                {at.questions.map((q: any) => {
                                    const answered = q.user_answer !== null && q.user_answer !== '';
                                    return (
                                        <Box key={q.question_order} sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle2">Q{q.question_order}</Typography>
                                                <Chip
                                                    size="small"
                                                    color={!answered ? 'default' : q.is_correct ? 'success' : 'error'}
                                                    label={!answered ? 'Blank' : q.is_correct ? 'Right' : 'Wrong'}
                                                />
                                                <Typography variant="body2">
                                                    Answered <b>{answerLabel(q.user_answer, q.question_type)}</b>, key{' '}
                                                    <b>{answerLabel(q.correct_answer, q.question_type)}</b> · {q.marks_obtained} of {q.marks} marks ·{' '}
                                                    {q.time_spent}s
                                                </Typography>
                                            </Box>
                                            <QuestionView q={q.content} answer={q.correct_answer} given={q.user_answer} showSolution />
                                            <Divider sx={{ mt: 2 }} />
                                        </Box>
                                    );
                                })}
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
